import { useCurrency } from "@/lib/currency";
// Desktop-only redesign for the tour detail page (lg+).
// Renders a 2-column hero (gallery + sticky mini booking card), a sticky
// section subnav, and a 3-column content grid inspired by GetYourGuide /
// Airbnb Experiences / Viator. All copy comes from the same i18n keys and
// data structures used by the existing mobile/tablet layout — nothing is
// hidden or removed.
import { categoryLabel } from "@/lib/category-label";
import { CancellationPolicyBlock } from "./CancellationPolicyBlock";
import { Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Clock, MapPin, Check, X, Users, Sparkles, Info, AlertTriangle,
  ChevronDown, ArrowLeft, Bus, Globe2, Ticket, Baby, Backpack, ShieldCheck,
  Heart, ExternalLink,
} from "lucide-react";

import { formatPrice } from "@/lib/slug";
import { ProductGalleryLazy as ProductGallery } from "@/components/product/ProductGalleryLazy";
import { ShareButton } from "@/components/site/ShareButton";
import { BokunOfficialWidget } from "@/components/booking/BokunOfficialWidget";

import { CustomTourBookingPanel } from "@/components/tour/CustomTourBookingPanel";
import { DestinationDiscovery } from "@/components/tour/DestinationDiscovery";
import { FreeDestinationGuideDownload } from "@/components/tour/FreeDestinationGuideDownload";
import { MayaAudioGuide } from "@/components/maya/MayaAudioGuide";
import { MAYA_PREMIUM_ENABLED } from "@/lib/maya-feature-flag";
import { ReviewsSection } from "@/components/tour/ReviewsSection";
import { formatDuration, stripHtml } from "@/lib/slug";
import type { UnifiedTourDetail } from "@/lib/catalog.functions";

interface Props {
  tour: UnifiedTourDetail;
  slug: string;
  lang: string;
  summary: string;
  desc: string;
}

const NAV_OFFSET = 72; // sticky offset for subnav & scroll

function fmtPrice(v: number | null | undefined, currency: string) {
  if (v == null) return null;
  // Delegates to the centralized converter (source currency -> selected currency).
  return formatPrice(v, currency);
}


function scrollToId(id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.scrollY - NAV_OFFSET - 12;
  window.scrollTo({ top, behavior: "smooth" });
}

export function TourDesktopLayout({ tour: p, slug, summary, desc, lang }: Props) {
  useCurrency(); // subscribe: fmtPrice reads the currency store
  const { t } = useTranslation();
  const isBokun = p.source === "bokun";
  const dur = p.durationText ?? formatDuration(p.durationMinutes);
  const meetingTypes = t("tour.meetingTypes", { returnObjects: true }) as Record<string, string>;
  const difficulties = t("tour.difficulties", { returnObjects: true }) as Record<string, string>;
  const priceLabel = fmtPrice(p.priceFrom, p.currency ?? "MXN");

  const sections = useMemo(() => {
    const items: { id: string; label: string }[] = [
      { id: "descripcion", label: t("tour.subnav.description", { defaultValue: lang === "fr" ? "Description" : "Descripción" }) },
    ];
    if (p.itinerary.length > 0) items.push({ id: "itinerario", label: t("tour.itinerary") });
    if (p.included.length > 0) items.push({ id: "incluye", label: t("tour.includes") });
    if (p.excluded.length > 0) items.push({ id: "no-incluye", label: t("tour.excludes") });
    if (p.startPoints.length > 0) items.push({ id: "mapa", label: t("tour.subnav.map", { defaultValue: lang === "fr" ? "Carte" : "Mapa" }) });
    items.push({ id: "opiniones", label: t("tour.subnav.reviews", { defaultValue: lang === "fr" ? "Avis" : "Opiniones" }) });
    return items;
  }, [p.itinerary.length, p.included.length, p.excluded.length, p.startPoints.length, lang, t]);

  return (
    <div className="tour-typography-desktop">
      <div className="mx-auto max-w-7xl px-6 pt-4 lg:px-8">
        {/* Breadcrumb / back */}
        <div className="mb-3 flex items-center justify-between gap-3 text-sm">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            {t("nav.backHome", "Volver al inicio")}
          </Link>
          <div className="flex items-center gap-2">
            <ShareButton url={`/tour/${slug}`} title={p.title} text={summary || desc.slice(0, 120)} />
          </div>
        </div>

        {/* ============ LAYOUT 2 COLUMNAS (contenido + sidebar sticky) ============ */}
        <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,360px)]">
          {/* ==== Columna principal (contenido) ==== */}
          <div className="min-w-0">
            {/* Encabezado + galería */}
            <div className="mb-3 flex flex-wrap items-center gap-2 text-[0.7rem] uppercase tracking-[0.08em]">
              {p.source === "custom" && (
                <span className="rounded-full bg-primary px-3 py-1 font-semibold text-primary-foreground">
                  Mostrando México
                </span>
              )}
              {p.categories.slice(0, 3).map((c) => (
                <span key={c} className="rounded-full bg-accent px-3 py-1 font-semibold text-accent-foreground">
                  {categoryLabel(c)}
                </span>
              ))}
            </div>

            <h1 className="tour-display mb-4 text-[2rem] leading-[1.05] lg:text-[2.25rem]">
              {p.title}
            </h1>

            <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-muted-foreground">
              {p.location && (
                <span className="inline-flex items-center gap-1.5">
                  <MapPin className="h-4 w-4" /> {p.location}
                </span>
              )}
              {dur && (
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="h-4 w-4" /> {dur}
                </span>
              )}
            </div>

            <div className="overflow-hidden rounded-2xl">
              <ProductGallery images={p.images} alt={p.title} />
            </div>

            {/* Subnav sticky */}
            <TourSubnav sections={sections} />

            {/* Secciones de contenido */}
            <div className="mt-6 space-y-4">
              <section id="descripcion" className="rounded-2xl border border-border/70 bg-card p-5">
                <h2 className="tour-display text-xl">{t("tour.subnav.description", { defaultValue: lang === "fr" ? "Description" : "Descripción" })}</h2>
                {summary && (
                  <p className="mt-3 text-[0.95rem] leading-[1.6] text-foreground/90">{summary}</p>
                )}
                {desc && <DescriptionCollapsible text={desc} />}
              </section>

              {p.highlights.length > 0 && (
                <section className="rounded-2xl border border-border/70 bg-card p-5">
                  <h3 className="tour-display inline-flex items-center gap-2 text-lg">
                    <Sparkles className="h-4 w-4 text-primary" /> {t("tour.highlights")}
                  </h3>
                  <ul className="mt-3 space-y-2 text-[0.92rem] leading-[1.55] text-foreground/85">
                    {p.highlights.map((h, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                        {stripHtml(h)}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {p.itinerary.length > 0 && (
                <section id="itinerario" className="rounded-2xl border border-border/70 bg-card p-5">
                  <h2 className="tour-display text-xl">{t("tour.itinerary")}</h2>
                  <ItineraryTimeline items={p.itinerary} />
                </section>
              )}

              {(p.included.length > 0 || p.excluded.length > 0) && (
                <div className="grid gap-6 lg:grid-cols-2 lg:items-stretch">
                  {p.included.length > 0 && (
                    <section id="incluye" className="flex h-full flex-col rounded-2xl border border-border/70 bg-card p-5">
                      <h3 className="tour-display text-lg">{t("tour.includes")}</h3>
                      <ul className="mt-3 space-y-2 text-[0.92rem] leading-[1.55]">
                        {p.included.map((x, i) => (
                          <li key={i} className="flex gap-2">
                            <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" /> {stripHtml(x)}
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}

                  {p.excluded.length > 0 && (
                    <section id="no-incluye" className="flex h-full flex-col rounded-2xl border border-border/70 bg-card p-5">
                      <h3 className="tour-display text-lg">{t("tour.excludes")}</h3>
                      <ul className="mt-3 space-y-2 text-[0.92rem] leading-[1.55] text-muted-foreground">
                        {p.excluded.map((x, i) => (
                          <li key={i} className="flex gap-2">
                            <X className="mt-0.5 h-4 w-4 shrink-0 text-destructive/70" /> {stripHtml(x)}
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}
                </div>
              )}



              {p.startPoints.length > 0 && (
                <section id="mapa" className="rounded-2xl border border-border/70 bg-card p-5">
                  <h3 className="tour-display inline-flex items-center gap-2 text-lg">
                    <MapPin className="h-4 w-4 text-primary" /> {t("tour.meetingPoint")}
                  </h3>
                  <ul className="mt-3 space-y-3">
                    {p.startPoints.map((sp) => (
                      <li key={sp.id} className="text-[0.92rem]">
                        <div className="font-semibold" style={{ fontFamily: '"Manrope","Inter",sans-serif' }}>
                          {sp.title}
                        </div>
                        {(sp.address || sp.city) && (
                          <div className="mt-0.5 text-muted-foreground">
                            {[sp.address, sp.city].filter(Boolean).join(", ")}
                          </div>
                        )}
                        {sp.latitude && sp.longitude && (
                          <a
                            href={`https://maps.google.com/?q=${sp.latitude},${sp.longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                          >
                            {t("common.seeOnMaps")} <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {(p.requirements || p.importantInfo || p.restrictions || p.childrenNotes || p.groupNotes) && (
                <section className="rounded-2xl border border-border/70 bg-card p-5">
                  <h3 className="tour-display inline-flex items-center gap-2 text-lg">
                    <Info className="h-4 w-4 text-primary" /> {t("tour.importantInfo")}
                  </h3>
                  {p.requirements && (
                    <div className="mt-3">
                      <h4 className="text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                        {t("tour.requirements")}
                      </h4>
                      <p className="mt-1.5 whitespace-pre-line text-[0.92rem] leading-[1.6] text-foreground/85">
                        {stripHtml(p.requirements)}
                      </p>
                    </div>
                  )}
                  {p.importantInfo && (
                    <div className="mt-3">
                      <h4 className="text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                        {t("tour.beforeBooking")}
                      </h4>
                      <p className="mt-1.5 whitespace-pre-line text-[0.92rem] leading-[1.6] text-foreground/85">
                        {stripHtml(p.importantInfo)}
                      </p>
                    </div>
                  )}
                  {[p.restrictions, p.childrenNotes, p.groupNotes].filter(Boolean).map((txt, i) => (
                    <p key={i} className="mt-3 whitespace-pre-line text-[0.92rem] leading-[1.6] text-foreground/85">
                      {stripHtml(txt as string)}
                    </p>
                  ))}
                </section>
              )}

              {p.source === "bokun" ? <CancellationPolicyBlock productId={p.bokunActivityId} /> : (p.cancellationPolicy || p.changePolicy || p.refundPolicy || p.terms) && (
                <section className="rounded-2xl border border-border/70 bg-card p-5">
                  <h3 className="tour-display inline-flex items-center gap-2 text-lg">
                    <AlertTriangle className="h-4 w-4 text-primary" /> {t("tour.cancellation")}
                  </h3>
                  {[p.cancellationPolicy, p.changePolicy, p.refundPolicy, p.terms].filter(Boolean).map((txt, i) => (
                    <p key={i} className="mt-2 whitespace-pre-line text-[0.92rem] leading-[1.6] text-foreground/85">
                      {stripHtml(txt as string)}
                    </p>
                  ))}
                </section>
              )}

              
              <DestinationDiscovery title={p.title} location={p.location} summary={p.summary} itinerary={p.itinerary} highlights={p.highlights} meetingPoints={p.startPoints.map((m) => m.title)} />

              {MAYA_PREMIUM_ENABLED && (
                <MayaAudioGuide
                  title={p.title}
                  location={p.location}
                  summary={summary || (desc ? desc.slice(0, 240) : null)}
                  tourSlug={slug}
                  itinerary={p.itinerary}
                  variant="premium"
                />
              )}

              <div id="opiniones">
                <ReviewsSection slug={slug} source={p.source} tourTitle={p.title} />
              </div>
            </div>
          </div>

          {/* ==== Sidebar sticky (reserva + info rápida) ==== */}
          <aside className="min-w-0">
            <div className="sticky top-[80px] space-y-4" id="reservar">
              <PremiumBookingCard
                priceLabel={priceLabel}
                dur={dur}
                meetingPoint={p.startPoints[0]?.title ?? (p.meetingType ? (meetingTypes[p.meetingType] ?? p.meetingType) : null)}
                hasFreeCancel={!!p.cancellationPolicy}
                slug={slug}
                title={p.title}
                shareText={summary || desc.slice(0, 120)}
                t={t}
              >
                {isBokun && p.bokunActivityId ? (
                  <BokunOfficialWidget productId={p.bokunActivityId} lang={lang} />
                ) : (
                  <CustomTourBookingPanel
                    tourId={p.id.replace(/^mx-/, "")}
                    fallbackPriceFrom={p.priceFrom ?? null}
                    fallbackCurrency={p.currency ?? "MXN"}
                    extras={p.bookingExtras ?? []}
                    questions={p.bookingQuestions ?? []}
                    meetingPoints={p.meetingPoints ?? []}
                    minParticipants={p.minBookable ?? null}
                    maxParticipants={p.maxBookable ?? null}
                    bookingCutoffHours={p.bookingCutoffHours ?? null}
                    requiresHotelInfo={!!p.requiresHotelInfo}
                    requiresFlightInfo={!!p.requiresFlightInfo}
                    hasTerms={!!p.terms}
                  />
                )}
              </PremiumBookingCard>

              <div className="[&>section]:mt-0">
                <FreeDestinationGuideDownload slug={slug} destinationName={p.location || p.title} />
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

/* ============================== Subcomponents ============================== */

function TourSubnav({ sections }: { sections: { id: string; label: string }[] }) {
  const [active, setActive] = useState<string | null>(null);

  useEffect(() => {
    if (sections.length === 0) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => (a.boundingClientRect.top - b.boundingClientRect.top));
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: `-${NAV_OFFSET + 20}px 0px -60% 0px`, threshold: [0, 1] },
    );
    for (const s of sections) {
      const el = document.getElementById(s.id);
      if (el) obs.observe(el);
    }
    return () => obs.disconnect();
  }, [sections]);

  return (
    <div className="sticky top-[64px] z-30 mt-6">
      <div className="rounded-2xl border border-border/60 bg-gradient-to-b from-white/95 to-[#F8F9FB]/95 px-2 py-1.5 shadow-[0_8px_24px_-18px_rgba(15,23,42,0.25)] backdrop-blur-md dark:from-slate-900/85 dark:to-slate-900/70">
        <nav className="flex items-center justify-between gap-1 overflow-x-auto">
          {sections.map((s) => {
            const isActive = active === s.id;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => scrollToId(s.id)}
                className={`relative flex-1 cursor-pointer whitespace-nowrap rounded-xl px-5 py-3 text-[0.9rem] font-semibold transition-all duration-200 ${
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-accent/60 hover:text-foreground"
                }`}
              >
                {s.label}
                <span
                  className={`pointer-events-none absolute inset-x-4 bottom-1 h-[3px] rounded-full bg-primary transition-all duration-300 ${
                    isActive ? "scale-x-100 opacity-100" : "scale-x-0 opacity-0"
                  }`}
                />
              </button>
            );
          })}
        </nav>
      </div>
    </div>
  );
}

function ItineraryTimeline({
  items,
}: {
  items: { index: number; title: string; body: string | null }[];
}) {
  const { t, i18n } = useTranslation();
  const isFrench = i18n.language.slice(0, 2).toLowerCase() === "fr";
  const [expanded, setExpanded] = useState(false);
  const LIMIT = 4;
  const hasMore = items.length > LIMIT;
  const shown = expanded ? items : items.slice(0, LIMIT);

  return (
    <div className="mt-3">
      <ol className="relative space-y-3.5 border-l-2 border-primary/20 pl-6">
        {shown.map((it, i) => (
          <li key={i} className="relative">
            <span className="absolute -left-[34px] top-0 inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary text-[11px] font-bold text-primary-foreground ring-4 ring-background">
              {i + 1}
            </span>
            <h4
              className="text-[0.95rem] font-semibold leading-tight"
              style={{ fontFamily: '"Manrope","Inter",sans-serif' }}
            >
              {it.title}
            </h4>
            {it.body && (
              <p className="mt-1 text-[0.88rem] leading-[1.55] text-foreground/75">
                {stripHtml(it.body)}
              </p>
            )}
          </li>
        ))}
      </ol>

      {hasMore && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-4 py-2 text-xs font-semibold text-foreground transition hover:border-primary/40 hover:bg-accent"
        >
          {expanded
            ? t("tour.showLess", { defaultValue: isFrench ? "Afficher moins" : "Mostrar menos" })
            : t("tour.itineraryShowAll", {
                defaultValue: isFrench ? "Voir l'itinéraire complet" : "Ver itinerario completo",
              })}
          <ChevronDown
            className={`h-3.5 w-3.5 transition-transform duration-300 ${expanded ? "rotate-180" : ""}`}
          />
        </button>
      )}
    </div>
  );
}

function QuickInfoCard({
  tour: p,
  dur,
  meetingTypes,
  difficulties,
}: {
  tour: UnifiedTourDetail;
  dur: string | null;
  meetingTypes: Record<string, string>;
  difficulties: Record<string, string>;
}) {
  const { t, i18n } = useTranslation();
  const isFrench = i18n.language.slice(0, 2).toLowerCase() === "fr";
  const langs = (p.guideLanguages?.length ? p.guideLanguages : p.audioLanguages) ?? [];

  const rows: { icon: React.ReactNode; label: string; value: string }[] = [];
  if (dur) rows.push({ icon: <Clock className="h-4 w-4" />, label: t("tour.duration", { defaultValue: isFrench ? "Durée" : "Duración" }), value: dur });
  if (p.meetingType) rows.push({ icon: <Bus className="h-4 w-4" />, label: t("tour.transport", { defaultValue: isFrench ? "Transport / Type" : "Transporte / Tipo" }), value: meetingTypes[p.meetingType] ?? p.meetingType });
  if (langs.length > 0) rows.push({ icon: <Globe2 className="h-4 w-4" />, label: t("tour.languages", { defaultValue: isFrench ? "Langues" : "Idiomas" }), value: langs.join(", ") });
  if (p.startPoints[0]) rows.push({ icon: <MapPin className="h-4 w-4" />, label: t("tour.meetingPoint"), value: p.startPoints[0].title });
  if (p.categories[0]) rows.push({ icon: <Ticket className="h-4 w-4" />, label: t("tour.category", { defaultValue: isFrench ? "Catégorie" : "Categoría" }), value: p.categories.slice(0, 2).join(" · ") });
  if (p.minAge != null && p.minAge > 0) rows.push({ icon: <Baby className="h-4 w-4" />, label: t("tour.minAge"), value: `${p.minAge}+` });
  if (p.difficultyLevel) rows.push({ icon: <ShieldCheck className="h-4 w-4" />, label: t("tour.difficulty"), value: difficulties[p.difficultyLevel] ?? p.difficultyLevel });
  if (p.dressCode) rows.push({ icon: <Backpack className="h-4 w-4" />, label: t("tour.whatToBring", { defaultValue: isFrench ? "À apporter" : "Qué llevar" }), value: stripHtml(p.dressCode).slice(0, 120) });

  return (
    <div className="rounded-2xl border border-border/70 bg-card p-5">
      <h3 className="tour-display mb-3 text-lg">
        {t("tour.quickInfo", { defaultValue: isFrench ? "Informations pratiques" : "Información rápida" })}
      </h3>
      <ul className="space-y-3 text-sm">
        {rows.map((r, i) => (
          <li key={i} className="flex items-start gap-2.5">
            <span className="mt-0.5 text-primary">{r.icon}</span>
            <div className="min-w-0 flex-1">
              <div className="text-[0.72rem] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                {r.label}
              </div>
              <div className="text-[0.9rem] text-foreground/90">{r.value}</div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function DescriptionCollapsible({ text }: { text: string }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const paragraphs = useMemo(
    () => text.split(/\n+/).slice(0, 20).filter(Boolean),
    [text],
  );
  const preview = paragraphs.slice(0, 2);
  const rest = paragraphs.slice(2);
  const hasMore = rest.length > 0;

  return (
    <div className="mt-3 space-y-3 text-[0.92rem] leading-[1.6] text-foreground/85">
      {preview.map((para, i) => <p key={`p-${i}`}>{para}</p>)}
      {hasMore && (
        <>
          <div
            className={`grid transition-[grid-template-rows,opacity] duration-500 ease-out ${
              expanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
            }`}
            aria-hidden={!expanded}
          >
            <div className="min-h-0 overflow-hidden">
              <div className="space-y-3 pt-2">
                {rest.map((para, i) => <p key={`r-${i}`}>{para}</p>)}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
            aria-expanded={expanded}
          >
            {expanded
              ? t("tour.showLess", { defaultValue: "Mostrar menos" })
              : t("tour.readMore", { defaultValue: "Leer más" })}
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded ? "rotate-180" : ""}`} />
          </button>
        </>
      )}
    </div>
  );
}

/* --------------- Premium booking card (visual only) --------------- */
function PremiumBookingCard({
  priceLabel,
  dur,
  meetingPoint,
  hasFreeCancel,
  slug,
  title,
  shareText,
  t,
  children,
}: {
  priceLabel: string | null;
  dur: string | null;
  meetingPoint: string | null;
  hasFreeCancel: boolean;
  slug: string;
  title: string;
  shareText: string;
  t: (k: string, o?: any) => string;
  children: React.ReactNode;
}) {
  const [fav, setFav] = useState(false);
  return (
    <div className="relative">
      {/* halo suave detrás */}
      <div
        aria-hidden
        className="pointer-events-none absolute -inset-3 rounded-[32px] bg-[conic-gradient(from_140deg,rgba(242,96,0,0.18),rgba(168,85,247,0.14),rgba(59,130,246,0.14),rgba(242,96,0,0.18))] opacity-60 blur-2xl"
      />
      <div className="relative rounded-[24px] border border-white/60 bg-white/85 p-6 shadow-[0_25px_60px_-25px_rgba(15,23,42,0.35)] backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/70">
        {/* Precio + badge */}
        <div className="flex items-start justify-between gap-3">
          <div>
            {priceLabel && (
              <div className="mt-1 flex items-baseline gap-1.5">
                <span
                  className="text-[2.4rem] font-black leading-none text-primary"
                  style={{ fontFamily: '"Manrope","Inter",sans-serif', letterSpacing: "-0.02em" }}
                >
                  {priceLabel}
                </span>
                <span className="text-xs text-muted-foreground">
                  {t("tour.perPerson", { defaultValue: "por persona" })}
                </span>
              </div>
            )}
          </div>
          <span className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-white/70 px-3 py-1 text-[0.68rem] font-semibold text-foreground/80 shadow-sm backdrop-blur">
            <ShieldCheck className="h-3.5 w-3.5 text-primary" />
            {t("tour.secureBooking", { defaultValue: "Reserva segura" })}
          </span>
        </div>

        <div className="my-5 h-px bg-gradient-to-r from-transparent via-border to-transparent" />

        {/* Info tiles */}
        <div className="space-y-2.5">
          {dur && (
            <div className="group flex items-center gap-3 rounded-2xl border border-border/60 bg-white/80 px-4 py-3 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition duration-250 hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-[0_10px_25px_-15px_rgba(242,96,0,0.4)] dark:bg-slate-900/60">
              <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Clock className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-[0.68rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                  {t("tour.duration", { defaultValue: "Duración total" })}
                </div>
                <div className="truncate text-[0.92rem] font-semibold text-foreground">{dur}</div>
              </div>
            </div>
          )}
          {meetingPoint && (
            <div className="group flex items-center gap-3 rounded-2xl border border-border/60 bg-white/80 px-4 py-3 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition duration-250 hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-[0_10px_25px_-15px_rgba(242,96,0,0.4)] dark:bg-slate-900/60">
              <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <MapPin className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-[0.68rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                  {t("tour.meetingPoint", { defaultValue: "Punto de encuentro" })}
                </div>
                <div className="line-clamp-2 text-[0.88rem] text-foreground/90">{meetingPoint}</div>
              </div>
            </div>
          )}
          {hasFreeCancel && (
            <div className="flex items-center gap-2 rounded-2xl bg-emerald-500/8 px-4 py-2.5 text-[0.82rem] font-medium text-emerald-700 dark:text-emerald-400">
              <ShieldCheck className="h-4 w-4" />
              {t("tour.freeCancellation", { defaultValue: "Cancelación gratuita" })}
            </div>
          )}
        </div>

        {/* Booking widget (calendario + categorías + Reservar ahora) */}
        <div className="mt-5 rounded-2xl border border-border/60 bg-white/70 p-3 shadow-inner backdrop-blur dark:bg-slate-900/40">
          {children}
        </div>

        {/* Favorito | Compartir */}
        <div className="mt-4 grid grid-cols-[1fr_auto_1fr] items-center gap-2 text-[0.85rem]">
          <button
            type="button"
            onClick={() => setFav((v) => !v)}
            className={`inline-flex items-center justify-center gap-1.5 rounded-full py-2 font-medium transition ${
              fav ? "text-primary" : "text-muted-foreground hover:text-foreground"
            }`}
            aria-pressed={fav}
          >
            <Heart className={`h-4 w-4 transition ${fav ? "fill-primary" : ""}`} />
            {t("tour.favorite", { defaultValue: "Favorito" })}
          </button>
          <span className="h-5 w-px bg-border" aria-hidden />
          <div className="flex items-center justify-center">
            <ShareButton url={`/tour/${slug}`} title={title} text={shareText} />
          </div>
        </div>
      </div>
    </div>
  );
}
